import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ArrowRight, Gift, Sparkles } from "lucide-react";
import StoreLayout from "../components/StoreLayout";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";

// Fallback ladder if the API is unreachable — kept in sync with backend DISCOUNT_LADDER.
const FALLBACK_LADDER = [
  { min_qty: 3, amount: 1000 },
  { min_qty: 2, amount: 500 },
  { min_qty: 1, amount: 250 },
];

function discountForQty(qty, ladder) {
  for (const tier of ladder) {
    if (qty >= tier.min_qty) return tier.amount;
  }
  return 0;
}

export default function Cart() {
  const { items, removeItem, updateQty, total } = useCart();
  const navigate = useNavigate();
  const [ladder, setLadder] = useState(FALLBACK_LADDER);

  useEffect(() => {
    api.get("/promo/discount-ladder")
      .then((r) => Array.isArray(r.data?.ladder) && setLadder(r.data.ladder))
      .catch(() => { /* fallback stays */ });
  }, []);

  // Sorted ascending for display (Buy 1 → Buy 2 → Buy 3)
  const displayLadder = useMemo(
    () => [...ladder].sort((a, b) => a.min_qty - b.min_qty),
    [ladder]
  );

  const totalUnits = useMemo(() => items.reduce((a, b) => a + b.qty, 0), [items]);
  const discount = useMemo(() => {
    const raw = discountForQty(totalUnits, ladder);
    // Never let discount exceed subtotal (matches backend clamp: min ₹1 charge)
    return Math.min(raw, Math.max(0, total - 1));
  }, [totalUnits, ladder, total]);

  const grandTotal = Math.max(1, total - discount);

  // Compute next tier to nudge upsell
  const nextTier = useMemo(() => {
    return displayLadder.find((t) => totalUnits < t.min_qty);
  }, [displayLadder, totalUnits]);

  const goCheckout = () => {
    navigate("/checkout");
  };

  return (
    <StoreLayout>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <p className="label-mono text-[#71717A]">// BAG</p>
        <h1 className="font-display font-black text-5xl md:text-7xl uppercase tracking-tight mt-3 mb-10">Your Cart</h1>

        {items.length === 0 ? (
          <div className="border border-[#27272A] py-20 text-center" data-testid="cart-empty">
            <p className="label-mono text-[#71717A]">// BAG IS EMPTY</p>
            <p className="font-display font-bold text-2xl mt-4">Nothing here yet</p>
            <Link to="/shop" className="btn-neon mt-6 inline-flex" data-testid="shop-now-cta">SHOP NOW <ArrowRight size={16}/></Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-4">
              {/* BULK DISCOUNT LADDER */}
              <BulkLadder ladder={displayLadder} totalUnits={totalUnits} nextTier={nextTier} discount={discount} />

              {items.map((it) => (
                <div key={`${it.product_id}-${it.size}-${it.color}`} className="border border-[#27272A] p-4 flex gap-4 items-center" data-testid={`cart-item-${it.product_id}`}>
                  <div className="w-24 h-24 bg-[#111] overflow-hidden flex-shrink-0">
                    <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display font-bold">{it.name}</h3>
                    <p className="label-mono text-[#71717A] mt-1">{it.size} // {it.color}</p>
                    <div className="flex items-center gap-3 mt-3">
                      <div className="inline-flex border border-[#27272A]">
                        <button onClick={() => updateQty(it.product_id, it.size, it.color, Math.max(1, it.qty - 1))} className="w-8 h-8 hover:bg-[#111]"><Minus size={12}/></button>
                        <div className="w-8 h-8 flex items-center justify-center font-mono-cult border-x border-[#27272A] text-sm">{it.qty}</div>
                        <button onClick={() => updateQty(it.product_id, it.size, it.color, it.qty + 1)} className="w-8 h-8 hover:bg-[#111]"><Plus size={12}/></button>
                      </div>
                      <button onClick={() => removeItem(it.product_id, it.size, it.color)} className="text-[#71717A] hover:text-red-500" data-testid={`remove-${it.product_id}`}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <div className="font-mono-cult text-[#CFF048] font-bold">₹{(it.price * it.qty).toFixed(2)}</div>
                </div>
              ))}
            </div>
            <div className="bg-[#0A0A0A] border border-[#27272A] p-6 h-fit lg:sticky lg:top-24" data-testid="cart-summary">
              <p className="label-mono text-[#71717A]">// SUMMARY</p>
              <h3 className="font-display font-black text-2xl uppercase mt-2">Order Total</h3>
              <div className="mt-6 space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="label-mono text-[#71717A]">Subtotal · {totalUnits} item{totalUnits !== 1 ? "s" : ""}</span>
                  <span className="font-mono-cult">₹{total.toFixed(2)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between" data-testid="cart-discount-row">
                    <span className="label-mono text-[#CFF048] flex items-center gap-1">
                      <Gift size={12} /> BULK LOVE · BUY {totalUnits}
                    </span>
                    <span className="font-mono-cult text-[#CFF048]" data-testid="cart-discount-amount">− ₹{discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between"><span className="label-mono text-[#71717A]">Shipping</span><span className="font-mono-cult text-[#CFF048]">FREE</span></div>
                <div className="border-t border-[#27272A] pt-3 flex justify-between text-lg">
                  <span className="font-display font-bold">TOTAL</span>
                  <span className="font-mono-cult text-[#CFF048] font-bold" data-testid="cart-total">₹{grandTotal.toFixed(2)}</span>
                </div>
              </div>
              {nextTier && (
                <div className="mt-4 border border-dashed border-[#CFF048]/50 p-3" data-testid="cart-next-tier-nudge">
                  <p className="label-mono text-[10px] text-[#CFF048]">// UPSELL</p>
                  <p className="text-xs text-[#A1A1AA] mt-1">
                    Add <strong className="text-white">{nextTier.min_qty - totalUnits}</strong> more tee{nextTier.min_qty - totalUnits > 1 ? "s" : ""} and unlock
                    <strong className="text-[#CFF048]"> ₹{nextTier.amount}</strong> off (currently saving <strong>₹{discount}</strong>).
                  </p>
                </div>
              )}
              <button onClick={goCheckout} className="btn-neon w-full mt-6" data-testid="checkout-btn">
                CHECKOUT <ArrowRight size={16} />
              </button>
              <p className="label-mono text-[#71717A] mt-4 text-center">GUEST CHECKOUT - NO SIGN-IN NEEDED</p>
            </div>
          </div>
        )}
      </div>
    </StoreLayout>
  );
}

function BulkLadder({ ladder, totalUnits, nextTier, discount }) {
  return (
    <div className="border border-[#CFF048] bg-[#0A0A0A]" data-testid="bulk-ladder">
      <div className="px-4 py-3 border-b border-[#27272A] flex items-center justify-between flex-wrap gap-2">
        <p className="label-mono text-[#CFF048] flex items-center gap-2">
          <Sparkles size={14} /> // BULK LOVE · AUTO-APPLIED
        </p>
        {discount > 0 ? (
          <p className="label-mono text-[10px] text-[#CFF048] tracking-widest" data-testid="bulk-ladder-status">
            YOU&apos;RE SAVING <strong>₹{discount}</strong> ON THIS ORDER
          </p>
        ) : (
          <p className="label-mono text-[10px] text-[#71717A] tracking-widest">
            ADD TEES TO UNLOCK ₹250 – ₹1000 OFF
          </p>
        )}
      </div>
      <div className="grid grid-cols-3 divide-x divide-[#27272A]">
        {ladder.map((tier) => {
          const active = totalUnits >= tier.min_qty;
          const next = nextTier?.min_qty === tier.min_qty;
          return (
            <div
              key={tier.min_qty}
              className={`p-4 text-center transition-colors ${active ? "bg-[#CFF048] text-black" : next ? "bg-[#111]" : ""}`}
              data-testid={`bulk-tier-${tier.min_qty}`}
            >
              <p className={`label-mono text-[10px] ${active ? "text-black/70" : next ? "text-[#CFF048]" : "text-[#71717A]"}`}>
                // TIER 0{ladder.indexOf(tier) + 1}{next ? " · NEXT" : active ? " · UNLOCKED" : ""}
              </p>
              <p className={`font-display font-black text-xl md:text-2xl mt-1 leading-none ${active ? "text-black" : "text-white"}`}>
                BUY {tier.min_qty}{tier.min_qty === ladder[ladder.length - 1].min_qty ? "+" : ""}
              </p>
              <p className={`font-mono-cult font-bold text-lg mt-1 ${active ? "text-black" : "text-[#CFF048]"}`}>
                ₹{tier.amount} OFF
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
